import axios from "axios";

/**
 * Cache GET leggera lato client con deduplica delle richieste "in volo".
 *
 * Motivazione (ottimizzazione caricamento 01/07/2026):
 * - Alcuni endpoint semi-statici (`pipeline-stages`, `insurances-list`,
 *   `treatments`, `ghl/doctors`, `ghl/assistants`) venivano richiamati più
 *   volte nello STESSO render (es. la Dashboard scaricava `pipeline-stages`
 *   3 volte da 3 sotto-componenti) e ri-scaricati da zero ad OGNI
 *   navigazione tra le pagine.
 * - `cachedGet` risolve entrambi:
 *   1) DEDUP: richieste identiche concorrenti condividono la stessa Promise
 *      → una sola chiamata di rete invece di N.
 *   2) TTL CACHE: la risposta è riusata per `ttl` ms tra le navigazioni.
 *
 * Ritorna un oggetto `{ data }` (stessa shape di axios) così i chiamanti
 * possono sostituire `axios.get(...)` con `cachedGet(...)` senza modifiche.
 *
 * NB: da usare SOLO su GET di dati semi-statici/idempotenti. Per dati che
 * cambiano ad ogni azione (liste appuntamenti, ricerche filtrate) continuare
 * a usare `axios.get` diretto.
 */
const _cache = new Map();    // key -> { ts, data }
const _inflight = new Map(); // key -> Promise<{ data }>

const DEFAULT_TTL = 90_000; // 90s

function _key(url, params) {
  return params ? `${url}?${JSON.stringify(params)}` : url;
}

export async function cachedGet(url, { params, ttl = DEFAULT_TTL, withCredentials = true } = {}) {
  const key = _key(url, params);
  const now = Date.now();

  const hit = _cache.get(key);
  if (hit && now - hit.ts < ttl) {
    return { data: hit.data };
  }

  const pending = _inflight.get(key);
  if (pending) return pending;

  const promise = axios
    .get(url, { params, withCredentials })
    .then((res) => {
      _cache.set(key, { ts: Date.now(), data: res.data });
      _inflight.delete(key);
      return { data: res.data };
    })
    .catch((err) => {
      _inflight.delete(key);
      throw err;
    });

  _inflight.set(key, promise);
  return promise;
}

/** Invalida una entry (o tutta la cache) — utile dopo una mutazione che
 *  cambia i dati semi-statici (es. modifica listino). */
export function invalidateCache(url, params) {
  if (!url) {
    _cache.clear();
    return;
  }
  _cache.delete(_key(url, params));
}
